import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { regionSetting } from '@pepperhq/regions';

import { uploadFile } from 'components/content/uploadFile';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { parseIntOrUndefined } from 'lib/helpers';
import logger from 'lib/logger';
import { ApplicationState } from 'store/store';
import { ILocationScopedSettingsComponentProps } from '../LocationScopedSettings';
import { tenantApi } from 'components/merchant/tenantApi';
import { Tenant } from 'components/merchant/models/Tenant';
import { useTwilioNumbers } from '../hooks/useTwilioNumbers';
import { enqueueSnackbar } from 'store/notifications/notificationsActions';
import { EBarcodePrefix, GeneralSettingsCard, getBarcodePrefixValue } from './GeneralSettingsCard';
import { AppsSettingsCard } from './AppsSettingsCard';
import { TwilioSettingsCard } from './TwilioSettingsCard';
import { AppleGoogleSSOSettings } from './AppleGoogleSSOSettings';
import { AppVersionSettings } from './AppVersionSettings';
import { MenuSupplementProviderSettings, EMenuSupplementProvider } from './MenuSupplementProviderSettings';

// Standard Regional Settings per country, these should be unchangable
const regionSettings: { [regionCode: string]: IPublicAndPrivateSettings['region'] } = regionSetting;

export const GlobalSettings: React.FC<ILocationScopedSettingsComponentProps> = ({
    settings,
    onSettingsSaved,
    locationId
}) => {
    const dispatch = useDispatch();
    const [tenant, setTenant] = React.useState<Tenant | null>(null);
    const { tenantId } = useSelector((state: ApplicationState) => state.auth.tenant);

    const {
        values: twilioNumbers,
        onChange: onTwilioNumbersChange,
        validate: validateTwilioNumbers
    } = useTwilioNumbers(settings?.twilio?.numbers);

    const handleFetchTenant = React.useCallback(async () => {
        const tenantResult = await tenantApi.get(tenantId);

        if (tenantResult.ok === true) {
            setTenant(tenantResult.body);
        }

        return tenantResult;
    }, [tenantId]);

    React.useEffect(() => {
        handleFetchTenant();
    }, [handleFetchTenant]);

    const handleSubmitGeneral = React.useCallback(
        async (values: Record<string, any>) => {
            try {
                const phoneAuthEnabled = !!values.identityProviders.find(
                    (provider: string) => provider === 'phone'
                );
                const facebookAuthEnabled = !!values.identityProviders.find(
                    (provider: string) => provider === 'facebook'
                );
                const emailAuthEnabled = !!values.identityProviders.find(
                    (provider: string) => provider === 'email'
                );
                if (values.faqMD) {
                    await uploadFile(
                        new File([await values.faqMD.text()], values.faqMD.name, {
                            type: 'text/markdown'
                        }),
                        'faq',
                        'app_assets'
                    );
                }
                if (values.termsMD) {
                    await uploadFile(
                        new File([await values.termsMD.text()], values.termsMD.name, {
                            type: 'text/markdown'
                        }),
                        'terms',
                        'app_assets'
                    );
                }
                if (values.privacyMD) {
                    await uploadFile(
                        new File([await values.privacyMD.text()], values.privacyMD.name, {
                            type: 'text/markdown'
                        }),
                        'privacy',
                        'app_assets'
                    );
                }
                if (values.aboutMD) {
                    await uploadFile(
                        new File([await values.aboutMD.text()], values.aboutMD.name, {
                            type: 'text/markdown'
                        }),
                        'about',
                        'app_assets'
                    );
                }
                let settingsToUpdate: IPublicAndPrivateSettings = {};

                // Common Settings
                if (locationId === undefined) {
                    const barcodePrefix =
                        values.userBarcodePrefixType === EBarcodePrefix.CUSTOM
                            ? values.userBarcodePrefix
                            : getBarcodePrefixValue(values.userBarcodePrefixType);
                    settingsToUpdate = {
                        ...settingsToUpdate,
                        phoneAuthEnabled,
                        facebookAuthEnabled,
                        emailAuthEnabled,
                        app: {
                            ageRequestEnabled: values.ageRequestEnabled,
                            registrationMinimumAge: parseIntOrUndefined(values.registrationMinimumAge),
                            registrationShowMarketingOptIn: values.registrationShowMarketingOptIn,
                            appId: values.appId,
                            showCopyright: values.showCopyright,
                            userBarcodeFormat: values.userBarcodeFormat,
                            userBarcodePrefix: barcodePrefix
                        },
                        region: {
                            code: values.region,
                            timezone: values.timezone,
                            taxLabel: values.taxLabel,
                            locale: values.locale
                        },
                        iam: {
                            appUserAuth: values.appUserAuth === 'LEGACY' ? null : values.appUserAuth
                        },
                        uniqueDeviceVerificationEnabled: values.uniqueDeviceVerificationEnabled,
                        phoneAuth: {
                            supportedCountryCallingCodes: values.supportedCountryCallingCodes?.map(
                                (pair: any) => pair.value
                            )
                        },
                        offlineCheckinEnabled: values.offlineCheckinEnabled,
                        secondaryCredentialsEnabled: values.secondaryCredentialsEnabled,
                        inAppReviewPromptsEnabled: values.inAppReviewPromptsEnabled
                    };

                    if (settingsToUpdate.region.code) {
                        settingsToUpdate.region = {
                            ...settingsToUpdate.region,
                            ...regionSettings[settingsToUpdate.region.code]
                        };
                    }
                }
                if (!Object.keys(settingsToUpdate)) {
                    // No need to update nothing
                    return true;
                }
                await new LocationSettingsApi().updatePublicAndPrivateSettings(
                    settingsToUpdate,
                    locationId,
                    true
                );
                await onSettingsSaved();
                return true;
            } catch (err) {
                logger.error(`Error Updating Settings: ${JSON.stringify(err)}`);
                return false;
            }
        },
        [locationId, onSettingsSaved]
    );

    const handleSubmitAndroidIos = React.useCallback(
        async (values: Record<string, any>) => {
            try {
                let settingsToUpdate: IPublicAndPrivateSettings = {};
                if (values.androidFirebase) {
                    await uploadFile(
                        new File([await values.androidFirebase.text()], values.androidFirebase.name, {
                            type: 'application/json'
                        }),
                        'google-services',
                        'app_config'
                    );
                }
                if (values.iOSFirebase) {
                    await uploadFile(
                        new File([await values.iOSFirebase.text()], values.iOSFirebase.name, {
                            type: 'application/x-plist'
                        }),
                        'GoogleService-Info',
                        'app_config'
                    );
                }

                // Common Settings
                if (locationId === undefined) {
                    settingsToUpdate = {
                        ...settingsToUpdate,
                        app: {
                            displayName: values.displayName,
                            bundleIdentifier: values.bundleIdentifier,
                            bundleDisplayName: values.bundleDisplayName,
                            bundleName: values.bundleName,
                            appURLScheme: values.appURLScheme,
                            appStoreUrl: values.appStoreUrl,
                            googleMapsApiKey: values.googleMapsApiKey,
                            playStoreUrl: values.playStoreUrl,
                            environment: values.environment
                        },
                        appleDeviceCheck: {
                            keyId: values.appleKeyId,
                            teamId: values.appleTeamId,
                            keyAsBase64: values.appleKeyFile
                        },
                        web: {
                            domainName: values.domainName
                        },
                        firebase: {
                            credentialsAsBase64: values.firebaseCredentialsFile
                        },
                        smartlookEnabled: values.smartlookEnabled
                    };
                }
                if (!Object.keys(settingsToUpdate).length) {
                    // No need to update nothing
                    return true;
                }
                await new LocationSettingsApi().updatePublicAndPrivateSettings(settingsToUpdate, locationId);
                await onSettingsSaved();
                return true;
            } catch (err) {
                logger.error(`Error Updating Settings: ${JSON.stringify(err)}`);
                return false;
            }
        },
        [locationId, onSettingsSaved]
    );
    const handleSubmitTwilio = React.useCallback(
        async (values: Record<string, any>) => {
            let numbers: Record<string, string[]> = {};
            try {
                numbers = validateTwilioNumbers();
            } catch (e) {
                dispatch(enqueueSnackbar(e.message, { variant: 'error' }));
                return false;
            }
            try {
                let settingsToUpdate: IPublicAndPrivateSettings = {};
                if (locationId === undefined) {
                    settingsToUpdate = {
                        ...settingsToUpdate,
                        twilio: {
                            accountSID: values.accountSID,
                            number: values.number,
                            authToken: values.authToken,
                            numbers: Object.keys(numbers).length ? numbers : null
                        }
                    };
                }
                if (!Object.keys(settingsToUpdate).length) {
                    // No need to update nothing
                    return true;
                }
                await new LocationSettingsApi().updatePublicAndPrivateSettings(settingsToUpdate, locationId);
                await onSettingsSaved();
                return true;
            } catch (err) {
                logger.error(`Error Updating Settings: ${JSON.stringify(err)}`);
                return false;
            }
        },
        [dispatch, locationId, onSettingsSaved, validateTwilioNumbers]
    );
    const handleSSOSubmit = React.useCallback(
        async (values: Record<string, any>) => {
            try {
                let settingsToUpdate: IPublicAndPrivateSettings = {};
                if (locationId === undefined) {
                    settingsToUpdate = {
                        appleSSOEnabled: values.appleSSOEnabled,
                        googleSSOEnabled: values.googleSSOEnabled,
                        appleSSO: {
                            webClientId: values.appleSSO_webClientId
                        },
                        googleSSO: {
                            iosClientId: values.googleSSO_iosClientId,
                            webClientId: values.googleSSO_webClientId,
                            webAppClientId: values.googleSSO_webAppClientId
                        }
                    };
                }
                if (!Object.keys(settingsToUpdate).length) {
                    // No need to update anything
                    return true;
                }
                await new LocationSettingsApi().updatePublicAndPrivateSettings(settingsToUpdate, locationId);
                await onSettingsSaved();
                return true;
            } catch (err) {
                logger.error(`Error Updating Settings: ${JSON.stringify(err)}`);
                return false;
            }
        },
        [locationId, onSettingsSaved]
    );

    const handleSubmitAppVersions = React.useCallback(
        (appVersions: Tenant['appVersions']) => {
            const defaultedVersions = {
                ios: {
                    must: appVersions.ios.must || '0.0.0.0',
                    should: appVersions.ios.should || '0.0.0.0'
                },
                android: {
                    must: appVersions.android.must || '0.0.0.0',
                    should: appVersions.android.should || '0.0.0.0'
                }
            };
            return tenantApi.update(tenantId, { body: { appVersions: defaultedVersions } }).then(res => {
                if (res.ok) {
                    handleFetchTenant();

                    return true;
                }

                return false;
            });
        },
        [handleFetchTenant, tenantId]
    );

    const handleSubmitTenKites = React.useCallback(
        async (values: Record<string, any>) => {
            try {
                let settingsToUpdate: IPublicAndPrivateSettings = {};
                if (locationId === undefined) {
                    settingsToUpdate = {
                        ...settingsToUpdate,
                        tenKites: {
                            tenKitesOrgId: values.tenKitesOrgId,
                            menuId: values.menuId,
                            apiKey: values.apiKey,
                            nutritionUnit: values.nutritionUnit
                        },
                        menuSupplementProvider:
                            values.menuSupplementProvider === EMenuSupplementProvider.NONE
                                ? null
                                : values.menuSupplementProvider
                    };
                }
                if (!Object.keys(settingsToUpdate).length) {
                    // No need to update anything
                    return true;
                }
                await new LocationSettingsApi().updatePublicAndPrivateSettings(settingsToUpdate, locationId);
                await onSettingsSaved();
                return true;
            } catch (err) {
                logger.error(`Error Updating Settings: ${JSON.stringify(err)}`);
                return false;
            }
        },
        [locationId, onSettingsSaved]
    );

    return (
        <Box minWidth="500px">
            {/* Cards for each settings block */}
            <GeneralSettingsCard settings={settings} tenantId={tenantId} onSubmit={handleSubmitGeneral} />
            <AppsSettingsCard settings={settings} tenantId={tenantId} onSubmit={handleSubmitAndroidIos} />
            <TwilioSettingsCard
                settings={settings}
                values={twilioNumbers}
                onChange={onTwilioNumbersChange}
                onSubmit={handleSubmitTwilio}
            />
            <AppleGoogleSSOSettings settings={settings} onSubmit={handleSSOSubmit} />
            <AppVersionSettings tenant={tenant} onSubmit={handleSubmitAppVersions} />
            <MenuSupplementProviderSettings settings={settings} onSubmit={handleSubmitTenKites} />
        </Box>
    );
};
